/**
 * Multi-value filter select (RM-118). Built on the shadcn/Base UI Select in
 * `multiple` mode: the value is an array, the popup stays open while toggling,
 * and an empty selection means "all". The trigger shows the chosen labels (or
 * `allLabel` when empty). Options can carry a count badge.
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select'

export interface MultiSelectOption {
  value: string
  label: string
  /** Optional right-aligned count badge (hidden when 0/undefined). */
  count?: number
  /** Optional leading dot — a Tailwind background-color class (e.g. status hue). */
  dot?: string
  /** data-testid for the option row (parity contract). */
  testId?: string
}

export function MultiSelect({
  value,
  onChange,
  options,
  allLabel,
  triggerTestId,
  className,
}: {
  value: string[]
  onChange: (value: string[]) => void
  options: MultiSelectOption[]
  /** Shown on the trigger when nothing is selected (= no filter). */
  allLabel: string
  triggerTestId?: string
  className?: string
}) {
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v

  return (
    <Select
      multiple
      value={value}
      onValueChange={(next) => onChange((next as string[] | null) ?? [])}
    >
      <SelectTrigger className={className} data-testid={triggerTestId}>
        <SelectValue placeholder={allLabel}>
          {(selected: string[]) =>
            selected.length === 0 ? allLabel : selected.map(labelOf).join(', ')
          }
        </SelectValue>
      </SelectTrigger>
      <SelectContent
        side="bottom"
        sideOffset={4}
        align="start"
        alignItemWithTrigger={false}
      >
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} data-testid={o.testId}>
            <span className="flex flex-1 items-center gap-2">
              {o.dot && (
                <span className={`size-2 shrink-0 rounded-full ${o.dot}`} />
              )}
              {o.label}
            </span>
            {o.count !== undefined && o.count > 0 && (
              <span className="ml-2 rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                {o.count}
              </span>
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
